import styled from '@emotion/styled'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Text } from '../..//Typography/Typography'
import { scale } from '../../../utils/scale'
import { ColumnFlex } from '../../Layout/Layout'
import { groupItems } from '../utils'

import SearchItem from './SearchItem'

const SearchItemGroup = styled(ColumnFlex)`
  margin-bottom: 12px;

  .MuiButton-root {
    margin-top: 16px;
  }
`

const SearchItemGroupTitle = styled(Text)`
  padding: 0 16px;
  margin-top: ${scale(3)};
  margin-bottom: ${scale(2)};
`

type SearchItem = {
  title: string
  onClick: () => void
  startIcon?: JSX.Element
  endIcon?: boolean
  description?: string
  group?: string
}

type Props = {
  items: SearchItem[]
  searchWord?: string
  autoFocus?: boolean | null
  headingId?: string
}

const ResultsFound: React.FC<Props> = ({ items, searchWord, autoFocus, headingId }) => {
  const { t } = useTranslation()
  const noGroupId = t('component.search.group.none')
  const itemsGrouped = groupItems(items, noGroupId)
  const groupKeys = Object.keys(itemsGrouped)
  const showGroups =
    groupKeys.length > 1 || (groupKeys.length === 1 && !groupKeys.includes(noGroupId))

  const indexRef = useRef<number>(-1)
  const itemEls = useRef<Array<HTMLElement | null>>([])

  useEffect(() => {
    if (itemEls.current[0] && autoFocus) {
      itemEls.current[0].focus()
    }
  }, [autoFocus])

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault()
        if (indexRef.current + 1 < items.length) {
          indexRef.current = indexRef.current + 1
          itemEls.current[indexRef.current]?.focus()
        }
        break
      }
      case 'ArrowUp': {
        e.preventDefault()
        if (indexRef.current - 1 >= 0) {
          indexRef.current = indexRef.current - 1
          itemEls.current[indexRef.current]?.focus()
        }
        break
      }
      case 'Home': {
        e.preventDefault()
        indexRef.current = 0
        itemEls.current[indexRef.current]?.focus()
        break
      }
      case 'End': {
        e.preventDefault()
        indexRef.current = items.length - 1
        itemEls.current[indexRef.current]?.focus()
        break
      }
      case 'Tab': {
        const increment = e.shiftKey ? -1 : 1
        if (indexRef.current + increment < items.length && indexRef.current + increment >= 0) {
          indexRef.current = indexRef.current + increment
        }
      }
    }
  }

  return (
    <ColumnFlex>
      {Object.entries(itemsGrouped).map(([group, groupedItems]) => (
        <SearchItemGroup key={`search-item-group-${group}`}>
          {showGroups && group !== noGroupId && (
            <SearchItemGroupTitle
              tabIndex={-1}
              id={`search-result-group-${group.replaceAll(' ', '-')}`}
              $size={400}
              $weight="Medium"
              $height="Medium"
            >
              {group}
            </SearchItemGroupTitle>
          )}
          <ul
            onKeyDown={onMenuKeyDown}
            role="menubar"
            aria-labelledby={
              showGroups && group !== noGroupId
                ? `search-result-group-${group.replaceAll(' ', '-')}`
                : headingId
            }
            aria-orientation="vertical"
          >
            {groupedItems.map((item, index) => (
              <SearchItem
                title={item.title}
                onClick={item.onClick}
                startIcon={item.startIcon}
                endIcon={item.endIcon}
                description={item.description}
                searchWord={searchWord}
                data-cy="search-result-item"
                key={`search-result-${group}-list-block-${index}`}
                ref={(element) => itemEls.current.push(element)}
              />
            ))}
          </ul>
        </SearchItemGroup>
      ))}
    </ColumnFlex>
  )
}

export default ResultsFound
